import { 
  Mail, MessageSquare, Users2, Shield, BarChart3, Database, Globe, Lock, 
  CheckCircle, Clock, Settings, Smartphone, Leaf, Zap, Brain, Eye, Target, 
  Star, CreditCard, Building2, Package, Truck, TrendingUp, Activity, Microscope
} from 'lucide-react';
import { ProductConfigs } from './types';

export const additionalMissingBeautySolutionsConfigs: ProductConfigs = {
  // Marketing & Communication Solutions
  'klaviyo-beauty-marketing': {
    keyBenefits: [
      {
        icon: Mail,
        title: 'Beauty Email Marketing Automation',
        description: 'Advanced email marketing platform specifically designed for beauty brands with personalized campaigns and customer journey automation.',
        metrics: '95% email deliverability rate',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users2,
        title: 'Beauty Customer Segmentation',
        description: 'Sophisticated customer segmentation based on beauty preferences, purchase history, and behavioral patterns.',
        metrics: '90% segmentation accuracy',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Personalized Beauty Campaigns',
        description: 'AI-powered personalized marketing campaigns with product recommendations and beauty trend insights.',
        metrics: '300% campaign effectiveness increase',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: TrendingUp,
        title: 'Beauty Marketing Analytics',
        description: 'Comprehensive analytics tracking beauty campaign performance, customer lifetime value, and ROI optimization.',
        metrics: 'Real-time marketing insights',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Beauty Engagement',
        description: 'Optimized mobile marketing experience with SMS campaigns, push notifications, and mobile-first design.',
        metrics: '4.8/5 mobile engagement rating',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Star,
        title: 'Beauty Loyalty Program Integration',
        description: 'Advanced loyalty program management with beauty-specific rewards, tier benefits, and retention strategies.',
        metrics: '85% customer retention improvement',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Beauty Predictions',
        description: 'Machine learning algorithms predicting beauty trends, customer preferences, and optimal campaign timing.',
        metrics: '89% prediction accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Campaign Optimization',
        description: 'Dynamic campaign optimization based on real-time performance data and customer engagement patterns.',
        metrics: 'Real-time optimization capability',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Beauty Workflow Builder',
        description: 'Flexible workflow automation enabling custom beauty marketing processes and customer journey mapping.',
        metrics: 'Unlimited workflow customisation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Global Beauty Market Intelligence',
        description: 'International beauty market insights with localization support and regional trend analysis.',
        metrics: 'Support for 50+ markets',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Package,
        title: 'Product-Specific Marketing',
        description: 'Advanced product-specific marketing with beauty category optimization and seasonal campaign management.',
        metrics: '95% product marketing effectiveness',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Activity,
        title: 'Beauty Customer Journey Analytics',
        description: 'Comprehensive customer journey tracking with beauty-specific touchpoints and conversion optimization.',
        metrics: 'Complete journey visibility',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'medshift-velocity-platform': {
    keyBenefits: [
      {
        icon: Building2,
        title: 'Medical Aesthetics Business Platform',
        description: 'Comprehensive business management platform specifically designed for medical aesthetic practices and medspas.',
        metrics: '95% medical aesthetics success rate',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Microscope,
        title: 'Advanced Treatment Management',
        description: 'Sophisticated treatment tracking and management system for medical aesthetic procedures and patient care.',
        metrics: '98% treatment tracking accuracy',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Shield,
        title: 'Medical Compliance & Safety',
        description: 'Advanced compliance management ensuring adherence to medical regulations and safety protocols.',
        metrics: '100% medical compliance',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Users2,
        title: 'Patient Experience Excellence',
        description: 'Comprehensive patient management with treatment history, consultation notes, and personalized care plans.',
        metrics: '92% patient satisfaction improvement',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Medical Practice Analytics',
        description: 'Advanced analytics for medical aesthetic practices with revenue optimization and performance tracking.',
        metrics: '75% practice revenue increase',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: CreditCard,
        title: 'Medical Billing & Payments',
        description: 'Specialized billing system for medical aesthetic procedures with insurance handling and payment plans.',
        metrics: '99.9% billing accuracy',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Medical Insights',
        description: 'Machine learning algorithms providing medical insights, treatment recommendations, and outcome predictions.',
        metrics: '94% predictive accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Eye,
        title: 'Medical Imaging Integration',
        description: 'Advanced medical imaging integration for before/after documentation and treatment progress tracking.',
        metrics: 'Professional medical imaging quality',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Medical Protocols',
        description: 'Flexible medical protocol customisation for different treatments, procedures, and patient requirements.',
        metrics: 'Unlimited protocol customisation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Medical Management',
        description: 'Centralized management for medical aesthetic chains with unified patient records and treatment protocols.',
        metrics: 'Seamless multi-location operations',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Medical Platform',
        description: 'Complete mobile solution for medical professionals with patient access, scheduling, and treatment management.',
        metrics: '4.9/5 medical mobile rating',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Activity,
        title: 'Medical Performance Optimization',
        description: 'Advanced performance analytics optimizing medical practice efficiency and patient outcomes.',
        metrics: 'Complete medical performance insights',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};